import axios, { AxiosInstance, AxiosRequestConfig } from "axios";
import { LocalFindAllParams } from "./DataSource";

export class PageableResult<DATA_TYPE> {
  public totalElements: number;
  public content: DATA_TYPE[];

  public constructor(content: DATA_TYPE[], total: number) {
    this.content = content;
    this.totalElements = total;
  }
}

export interface Api {
  getList<T>(url: string, data?: {}, config?: {}): Promise<PageableResult<T>>;
  getOne<T>(url: string, data?: {}, config?: {}): Promise<T>;
  save<T>(url: string, data?: {}, config?: {}): Promise<T>;
  update<T>(url: string, data?: {}, config?: {}): Promise<T>;
  partialUpdate<T>(url: string, data?: {}, config?: {}): Promise<T>;
  delete<T>(url: string, data?: {}, config?: {}): Promise<void>;
  getMethod<T>(url: string, config?: {}): Promise<T>;
  putMethod<T>(url: string, data?: {}, config?: {}): Promise<T>;
  patchMethod<T>(url: string, data?: {}, config?: {}): Promise<T>;
  postMethod<T>(url: string, data?: {}, config?: {}): Promise<T>;
  deleteMethod(url: string, data?: {}, config?: {}): Promise<void>;
  fileUpload(url: string, data?: {}, config?: {}): Promise<void>;
  fileDownload(
    url: string,
    params?: LocalFindAllParams,
    config?: AxiosRequestConfig
  ): Promise<void>;

  setCsrfToken(token: string): void;
  clearCsrfToken(): void;
  addErrorInterceptor(interceptor: (error: any) => any): void;
  setAcceptLanguage(lang: string): void;
}

export class RemoteApi implements Api {
  private static CSRF_TOKEN_HEADER = "CSRF-TOKEN";
  public readonly baseUrl: string;
  public readonly axios: AxiosInstance;

  public constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
    this.axios = axios.create({
      baseURL: this.baseUrl
    });
    this.axios.defaults.headers[
      RemoteApi.CSRF_TOKEN_HEADER
    ] = localStorage.getItem(RemoteApi.CSRF_TOKEN_HEADER);
  }

  public async getList<T>(
    url: string,
    { params, ...rest }: any
  ): Promise<PageableResult<T>> {
    const requestParams = this.pickFilters(params);
    const paging = this.pickPaging(params);
    const config = { params: { ...requestParams, ...paging }, ...rest };
    return await this.getMethod<PageableResult<T>>(url, config);
  }

  public async getOne<T>(url: string, config?: any): Promise<T> {
    return await this.getMethod<T>(url, config);
  }

  public async save<T>(url: string, data?: {}, config?: {}): Promise<T> {
    return await this.postMethod<T>(url, data, config);
  }

  public async update<T>(url: string, data?: {}, config?: {}): Promise<T> {
    return this.putMethod<T>(url, data, config);
  }

  public async partialUpdate<T>(
    url: string,
    data?: {},
    config?: {}
  ): Promise<T> {
    return this.patchMethod<T>(url, data, config);
  }

  public async delete(url: string, config?: {}): Promise<void> {
    await this.deleteMethod(url, config);
    return;
  }

  public async getMethod<T>(url: string, config?: {}): Promise<T> {
    const response = await this.axios.get<T>(url, config);
    return response.data;
  }

  public async putMethod<T>(url: string, data?: {}, config?: {}): Promise<T> {
    const response = await this.axios.put<T>(url, data, config);
    return response.data;
  }

  public async patchMethod<T>(url: string, data?: {}, config?: {}): Promise<T> {
    const response = await this.axios.patch<T>(url, data, config);
    return response.data;
  }

  public async postMethod<T>(url: string, data?: {}, config?: {}): Promise<T> {
    const response = await this.axios.post<T>(url, data, config);
    return response.data;
  }

  public async deleteMethod(url: string, config?: {}): Promise<void> {
    await this.axios.delete(url, config);
    return;
  }

  public async fileDownload(
    url: string,
    params?: LocalFindAllParams,
    config?: AxiosRequestConfig
  ): Promise<void> {
    let finalConfig: AxiosRequestConfig = { responseType: "blob", ...config };

    if (params) {
      const requestParams = this.transformParams(params);
      finalConfig = {
        params: requestParams,
        ...finalConfig
      };
    }
    const response = await this.axios.get(url, finalConfig);
    const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = blobUrl;
    const contentDisposition = response.headers["content-disposition"];
    let fileName = "file";
    if (contentDisposition) {
      const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
      if (fileNameMatch.length === 2) {
        fileName = fileNameMatch[1];
      }
    }
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return;
  }

  public async fileUpload(url: string, data: {}, config?: {}): Promise<void> {
    const form = new FormData();
    for (const key in data) {
      form.append(key, data[key]);
    }
    const finalConfig: AxiosRequestConfig = {
      headers: { "Content-Type": "multipart/form-data" },
      ...config
    };

    return await this.axios.post(url, form, finalConfig);
  }

  public setCsrfToken(token: string) {
    this.axios.defaults.headers[RemoteApi.CSRF_TOKEN_HEADER] = token;
    localStorage.setItem(RemoteApi.CSRF_TOKEN_HEADER, token);
  }

  public clearCsrfToken() {
    this.axios.defaults.headers[RemoteApi.CSRF_TOKEN_HEADER] = undefined;
    localStorage.removeItem(RemoteApi.CSRF_TOKEN_HEADER);
  }

  public addErrorInterceptor(interceptor: (error: any) => any) {
    this.axios.interceptors.response.use(v => v, interceptor);
  }

  public setAcceptLanguage(lang: string) {
    this.axios.defaults.headers["Accept-Language"] = lang;
  }

  private pickFilters(params: LocalFindAllParams) {
    const obj = {} as any;

    params.filters.forEach(filter => {
      obj[filter.field] = filter.value;
    });

    return obj;
  }

  private pickPaging(params: LocalFindAllParams) {
    const obj = {} as any;
    obj.page = params.page.pageNumber;
    obj.size = params.page.pageSize;
    obj.sort = `${params.sort.sort},${params.sort.order}`;
    return obj;
  }

  public transformParams(params: LocalFindAllParams) {
    const obj = {} as any;

    params.filters.forEach(filter => {
      obj[filter.field] = filter.value;
    });
    obj.page = params.page.pageNumber;
    obj.size = params.page.pageSize;
    obj.sort = `${params.sort.sort},${params.sort.order}`;

    return obj;
  }
}
